
/**
 *
 * @module helpers/viewModelHelpers
 * @author shojib [mahmud.hossain@mlb.com]
 * @description module for adding scores view-model helper functions
 *
 */

import HORIZONTAL_CONTROLS_BY_MODE_AND_DEVICE from '../../json/horizontal-controls-by-mode-and-device.json';

// Live games, then Pregame, then Final
const STATUS_ORDER = {
    L: {
        L: 0,
        P: -1,
        F: -1
    },
    P: {
        L: 1,
        P: 0,
        F: -1
    },
    F: {
        L: 1,
        P: 1,
        F: 0
    }
};

const GAME_TYPES = {
    F: 'Wild Card',
    D: 'Division Series',
    L: 'Championship Series',
    W: 'World Series',
    R: 'TieBreaker'
};

/**
 *
 * @name ViewModelHelpers
 * @description helper functions for ViewModel
 *
 */
const ViewModelHelpers = {

    /**
     * @function getGameKey
     * @desc Get key to use for game references. gamePK works for all single day modes
     *       calendarEventID is needed for MIG since gamePk is not guaranteed to be unique
     * @param {String} mode
     * @param {Object} gameData
     * @returns {String|Number} Either calendarEventID string or gamePk number
     */
    getGameKey(mode = 'responsive', gameData = {}) {

        if (mode === 'MIG') {

            return gameData.calendarEventID;

        } else {

            return gameData.gamePk;

        }

    },

    /**
     *
     * @function useControls
     * @desc Determine whether the scoreboard needs left/right controls based on
     * mode (minisb / gamepicker) and device (don't use with mobile devices)
     * @param opts
     * @returns {boolean}
     *
     */
    useControls(opts = {}) {

        const { mode, device } = opts;
        const controlsByDevice = HORIZONTAL_CONTROLS_BY_MODE_AND_DEVICE[mode];

        return !!(controlsByDevice && controlsByDevice[device]);

    },

    /**
     * @function getGameType
     * @description gets the gametype description from the dictionary and its fallback is static content stored in GAME_TYPES
     * @param gametype
     * @returns {string}
     */
    getGameType(gametype, dictionary) {

        let dictValue = '';
        let dictKey = 'postseason_score_round_header_';

        if (gametype && gametype !== '' && dictionary) {

            dictKey += `${gametype.toUpperCase()}`;
            dictValue = dictionary[dictKey] ? dictionary[dictKey] : GAME_TYPES[gametype].toUpperCase();

        }

        return dictValue;

    },

    /**
     * @function sortByGameStatus
     * @description For each date, sort the games by their status in STATUS_ORDER
     * then by gameDate value
     * @param mlbMoment
     * @param extendedData
     * @param teamId
     */
    sortByGameStatus(extendedData = {}, teamID = '', opts = {}) {

        const { dates } = extendedData;
        const { gameUtils, isPostseasonPush, postseasonPush } = opts;
        const fnCode = ['isLive', 'isDelayed', 'isPreview', 'isFinal', 'isSuspended', 'isPostponed', 'isCancelled'];

        gameUtils.sortByGameStatus(dates, teamID, fnCode);

        if (isPostseasonPush) {
            gameUtils.groupForPostSeasonPush(dates, postseasonPush, teamID);
        }

    },

        /**
     * @function sortGames
     * @description For each date, sort the games by their status in STATUS_ORDER
     * then by gameDate value
     * @param mlbMoment
     * @param extendedData
     * @param teamId
     */
    sortGames(mlbMoment, extendedData = {}, teamId = '', opts = {}) {
        const { gameUtils } = opts;
        gameUtils.sortGames(mlbMoment, extendedData, teamId, STATUS_ORDER);
    },

    /**
     * @function formatSeriesID
     * @description Format series ID
     * @param id
     */
    formatSeriesID(id) {
        let seriesId = id;

        if (id.length > 4) {

            seriesId = id.split(' ').shift();

        }

        return seriesId;

    },

    /**
     * @function groupGamesByTypes
     * @description Group games by postseason gametypes including tiebreaker
     * @param extendedData
     */
    groupGamesByTypes(extendedData = {}, opts = {}) {

        const { dictionary, lang } = opts;
        const { series } = extendedData;
        const postSeasonData = [];

        series.forEach((s) => {

            const games = {
                gameType: s.series.gameType,
                isDefault: s.series.isDefault,
                gameTypeDesc: ViewModelHelpers.getGameType(s.series.gameType, dictionary),
                sortOrder: s.sortOrder,
                games: s.games
            };

            if (postSeasonData.length === 0) {

                postSeasonData.push(games);

            } else {

                let fountGameType = false;

                postSeasonData.forEach((psData, index) => {

                    if (psData.gameType === s.series.gameType) {

                        postSeasonData[index].games = postSeasonData[index].games.concat(s.games);

                        fountGameType = true;

                    }

                });

                if (!fountGameType) {

                    postSeasonData.push(games);

                }
            }

        });

        return postSeasonData;

    },

    /**
     * @function setAds
     * @description Flag games according to adFrequency to insert an ad before that game
     * @param extendedData
     * @param adFrequency
     * ! Modifies data
     */
    setAds(extendedData = {}, adFrequency = 3) {

        const { dates = [] } = extendedData;
        let gameCount = 0;

        dates.forEach((date) => {

            const games = date.games || [];

            games.forEach((game) => {

                if ((gameCount > 0) && ((gameCount % adFrequency) === 0)) {

                    game.adAbove = true;

                    if (gameCount % (2 * adFrequency) === 0) {

                        game.evenAd = true;

                    }

                }

                gameCount = gameCount + 1;

            });
        });
    }

};

export default ViewModelHelpers;
